import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, RadioGroup } from '@mui/material';

import { BasicLayout, PlugNoData } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../Icons/arrow-right';
import { useGetJobListingTypesQuery } from '../../../../services/servicePostAJob';
import { IEventsStepProps } from '../../interfaceSteps';
import { useAppSelector } from '../../../../store/hooks';
import MultipleJobListing from './MultipleJobListing';
import { Step1Field } from './components/Step1Field';
import { IStep1Form } from './formInterface';
import { JobListingTypesResponse } from '../../../../services/servicePostAJob/responseInterfaces';
import { PromoCodeField } from '../../../PromoCodeField';
import { TOKENS } from '../../../../styles/tokens';
import StepTitle from '../../components/StepTitle';
import BenefitSelector from './BenefitSelector';
import { IJobBenefitSelection } from '../../../../services/servicePostAJob/responseInterfaces';
import { getJobCheckoutTotal, isJobCheckoutBlocked } from './checkoutGating';

interface IStep1Props extends IEventsStepProps {
  setJobListing: (a:IStep1Form) => void,
  setIsRequest: (a: boolean) => void,
  setIsInternalLoading: (a: boolean) => void,
  defaultData: IStep1Form,
  setJobListingTypes: (data:JobListingTypesResponse) => void,
  setBenefitSelection?: (selection: IJobBenefitSelection | null) => void,
}

export const Step1 = memo(({ onNext, setJobListing, defaultData, Nav, setIsRequest, setIsInternalLoading, setJobListingTypes, setBenefitSelection }:IStep1Props) => {
  const { siteNumber } = useAppSelector(({ pathnameSettings }) => ({
    siteNumber: pathnameSettings.siteNumber
  }));

  // Seed the promo code from the URL (?code=) — read once; editable state lives below.
  const urlCode = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('code') || undefined;
  }, []);

  // The code currently fed to the pricing query. Seeded from the URL, then driven
  // by the PromoCodeField Apply/Remove actions.
  const [appliedCoupon, setAppliedCoupon] = useState(urlCode);
  const [benefitQuoteBlocked, setBenefitQuoteBlocked] = useState(false);
  // A code definitively rejected by the current credit quote is dropped automatically so the
  // customer is never stuck behind a disabled Next; transient failures stay visible and retryable.
  const [removedCode, setRemovedCode] = useState<{ code: string; reason: string } | null>(null);
  // Bumped on auto-removal so PromoCodeField remounts empty (it seeds from initialCode once).
  const [promoFieldKey, setPromoFieldKey] = useState(0);
  const handleBenefitQuoteError = useCallback((message: string | null) => {
    if (!message || !appliedCoupon) return;

    setRemovedCode({ code: appliedCoupon, reason: message });
    setPromoFieldKey((key) => key + 1);
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    setAppliedCoupon(undefined);
  }, [appliedCoupon]);
  const handleApplyCode = useCallback((code: string | undefined) => {
    if (code) setRemovedCode(null);
    setAppliedCoupon(code);
  }, []);
  const [selectedBenefit, setSelectedBenefit] = useState<IJobBenefitSelection | null>(null);
  const handleBenefitSelection = useCallback((selection: IJobBenefitSelection | null) => {
    setSelectedBenefit(selection);
    setBenefitSelection?.(selection);
  }, [setBenefitSelection]);

  const { data: fetchedDataList, isLoading, isFetching } = useGetJobListingTypesQuery({
    id: siteNumber.toString(),
    code: appliedCoupon
  });
  // This RTK Query version retains the previous arguments' data while fetching. Keep
  // it for stable tile rendering, but never use it as authoritative checkout pricing.
  const pricingData = isFetching ? undefined : fetchedDataList;

  // No-code baseline (same endpoint, no `code`) for the savings diff. RTK dedupes
  // this against the coded query when no code is applied, so it costs an extra
  // request only while a code is active — and it is still fetched on a ?code= deep link.
  const { data: baselineData } = useGetJobListingTypesQuery({
    id: siteNumber.toString()
  });

  // "$X off applied" = baseline − with-code price for the SELECTED listing type
  // (Standard = 1, Featured = 2). Both prices carry any member discount, so the
  // delta is the code alone. Non-positive/unavailable → undefined (pill falls back
  // to "✓ Code applied", never "$0 off applied"). Recomputes on Standard↔Featured.
  const discountAmount = useMemo(() => {
    if (!pricingData || !baselineData) return undefined;
    const delta = defaultData.listingType === 1
      ? baselineData.standardPrice - pricingData.standardPrice
      : baselineData.featuredPrice - pricingData.featuredPrice;
    return delta > 0 ? delta : undefined;
  }, [pricingData, baselineData, defaultData.listingType]);

  // A scoped code (Standard-only or Featured-only — JobCouponsService ApplyToStandard/
  // ApplyToFeatured) can set the backend's aggregate `couponApplied` while NOT discounting
  // the *selected* type. Only show the green "applied" pill when the SELECTED type is
  // actually discounted; otherwise nudge the user toward the type the code covers, so we
  // never claim "✓ applied" while the tiles + payment stay at full price (P1).
  const standardDiscounted = !!pricingData && !!baselineData
    && baselineData.standardPrice - pricingData.standardPrice > 0;
  const featuredDiscounted = !!pricingData && !!baselineData
    && baselineData.featuredPrice - pricingData.featuredPrice > 0;
  const selectedTypeDiscounted = defaultData.listingType === 1 ? standardDiscounted : featuredDiscounted;
  const appliesToTypeName = standardDiscounted ? 'Standard' : (featuredDiscounted ? 'Featured' : '');
  const wrongTypeNote = pricingData?.couponApplied && !selectedTypeDiscounted && appliesToTypeName
    ? `This code applies to ${appliesToTypeName} listings. Select ${appliesToTypeName} to use it.`
    : undefined;

  // Footer "Total" = the selected listing's price (Standard=1 / Featured=2). The
  // green pill already surfaces the code savings and the tiles show list-price
  // strikes, so the footer stays a single clean amount (no extra strikethrough).
  const selectedPrice = isFetching ? undefined : getJobCheckoutTotal(
    defaultData.listingType,
    pricingData?.standardPrice,
    pricingData?.featuredPrice,
    selectedBenefit?.option.finalAmount,
  );

  const send = () => {
    if (onNext) {
      setIsRequest(true);
      onNext();
    }
  };

  const changeRadio = (a: any, val: string) => {
    // Preserve the applied code when switching listing type.
    setJobListing({ listingType: Number(val), couponCode: appliedCoupon });
  };

  useEffect(() => {
    setIsInternalLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (pricingData) setJobListingTypes(pricingData);
  }, [pricingData, setJobListingTypes]);

  useEffect(() => {
    // Persist the applied code (URL or typed) into step1 — and clear it when removed.
    setJobListing({ listingType: defaultData.listingType, couponCode: appliedCoupon });
  }, [appliedCoupon, defaultData.listingType]);

  return (
    <BasicLayout
      dataCy="step1"
      isHideChildren={isLoading}
      isLoading={isLoading}
    >
      <div className="cpg-step-card">
        <StepTitle
          title="Post a Single Job Listing"
          description="Choose between a Standard or Featured 60-Day Listing."
        />

        <RadioGroup
          className="cpg-tile-group"
          value={defaultData.listingType}
          onChange={(a, value) => changeRadio(a, value)}
        >
          {fetchedDataList ? (
            <>
              <Step1Field
                value={1}
                dataCy="jobListingStandard"
                currentPrice={fetchedDataList.standardPrice}
                extraPrice={fetchedDataList.listStandardPrice}
              />
              <Step1Field
                value={2}
                dataCy="jobListingFeatured"
                currentPrice={fetchedDataList.featuredPrice}
                extraPrice={fetchedDataList.listFeaturedPrice}
              />
            </>
          ) : <PlugNoData />}
        </RadioGroup>

        {setBenefitSelection && (
          <BenefitSelector
            siteId={siteNumber.toString()}
            listingType={defaultData.listingType}
            couponCode={appliedCoupon}
            onSelectionChange={handleBenefitSelection}
            onQuoteBlockedChange={setBenefitQuoteBlocked}
            onQuoteErrorChange={handleBenefitQuoteError}
          />
        )}

        {removedCode && (
          <Alert severity="warning" data-cy="removedCodeNotice" sx={{ mt: 2 }} onClose={() => setRemovedCode(null)}>
            <strong>{removedCode.code.toUpperCase()}</strong>
            {' '}
            {removedCode.reason.replace(/^this code /i, '')}
            {' '}
            The code was removed. You can continue without this code or enter a different one.
          </Alert>
        )}

        {/* Checkout footer (mockup): the Code box sits on the SAME line as the
            Total + Next button on desktop, and stacks on narrow screens. */}
        <Box
          className="cpg-checkout-footer"
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2.5,
            mt: 3,
            pt: '20px',
            borderTop: `1px solid ${TOKENS.lineSoft}`,
            '@media (max-width:700px)': {
              flexDirection: 'column',
              alignItems: 'stretch',
            },
          }}
        >
          <PromoCodeField
            key={promoFieldKey}
            initialCode={promoFieldKey === 0 ? urlCode : undefined}
            couponApplied={Boolean(pricingData?.couponApplied && selectedTypeDiscounted)}
            couponCode={pricingData?.couponCode}
            discountAmount={discountAmount}
            couponErrorMessage={pricingData?.couponErrorMessage}
            noteMessage={wrongTypeNote}
            isFetching={isFetching}
            onApply={handleApplyCode}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              '@media (max-width:700px)': {
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 1.5,
                width: '100%',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                whiteSpace: 'nowrap',
                '@media (max-width:700px)': {
                  flexDirection: 'row',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  width: '100%',
                },
              }}
            >
              <Box
                sx={{
                  fontFamily: TOKENS.fontDisplay,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: TOKENS.placeholder,
                }}
              >
                Total
              </Box>
              <Box
                data-cy="footerTotalPrice"
                sx={{
                  fontFamily: TOKENS.fontDisplay,
                  fontSize: 24,
                  fontWeight: 800,
                  lineHeight: 1.1,
                  color: TOKENS.ink,
                }}
              >
                {typeof selectedPrice === 'number' ? `$${selectedPrice}` : ''}
              </Box>
            </Box>

            <Box
              className="cpg-checkout-footer__nav"
              sx={{
                '& > div': { mt: 0 },
                '@media (max-width:700px)': { '& button': { width: '100%' } },
              }}
            >
              <Nav
                nextDisabled={false}
                onNext={onNext}
                nextButton={(
                  <Button
                    endIcon={(<ArrowRightIcon fontSize="small" />)}
                    variant="contained"
                    disabled={isJobCheckoutBlocked({
                      hasPricing: Boolean(pricingData && Object.keys(pricingData).length),
                      benefitQuoteBlocked,
                      appliedCoupon,
                      couponErrorMessage: pricingData?.couponErrorMessage,
                    })}
                    type="button"
                    onClick={send}
                    data-cy="nextStep"
                  >
                    Next step
                  </Button>
                )}
              />
            </Box>
          </Box>
        </Box>
      </div>

      <MultipleJobListing />
    </BasicLayout>
  );
});
